/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Fri Jun 21 2024
 **/

import React from "react";

import { Configure } from "../utilities";

export default function useClipboard(ref: React.RefObject<HTMLElement>) {
	const copy = async ({ callback = undefined }: { callback?: () => void }) => {
		try {
			const elm = ref.current as HTMLElement;

			// ref가 이미지 태그면 하위 요소가 없기때문에 outerHTML로 가져옴
			const htmlContent = isImgElement(elm) ? elm?.outerHTML : elm?.innerHTML;
			const textContent = elm?.textContent;
			if (!htmlContent) return;

			let clipboardItem;

			const hasImage = (htmlContent && htmlContent.indexOf('type="image"') !== -1) || isImgElement(elm);

			if (hasImage && !textContent) {
				const imageSrc = (isImgElement(elm) && elm.src) || elm?.getElementsByTagName("img")[0].src;
				const url = `${imageSrc}`;
				const response = await fetch(url, {
					headers: {
						Authorization: String(Configure.authorizationKey)
					}
				});
				const arrayBuffer = await response.arrayBuffer();

				clipboardItem = new ClipboardItem({
					"image/png": new Blob([arrayBuffer], { type: "image/png" })
				});
				await navigator.clipboard.write([clipboardItem]);
			} else if (textContent) {
				await navigator.clipboard.write([
					new ClipboardItem({
						"text/html": new Blob([htmlContent], { type: "text/html" }),
						"text/plain": new Blob([textContent], { type: "text/plain" })
					})
				]);
			}

			callback && callback();
		} catch (err) {
			console.debug("#useClipBoard# clipboard copy error", err);
		}
	};

	return {
		copy
	};
}

function isImgElement(elm: HTMLElement | null): elm is HTMLImageElement {
	return elm?.nodeName === "IMG";
}
